package com.macrofocus.eflux.components

import com.macrofocus.common.units.Megawatt
import com.macrofocus.eflux.network.crossborder.CrossborderBranches
import com.macrofocus.eflux.network.crossborder.CrossborderNetwork
import com.macrofocus.eflux.network.physical.Zone
import com.macrofocus.eflux.network.toFixed
import com.macrofocus.eflux.ui.Border
import com.macrofocus.eflux.ui.PaletteType
import com.macrofocus.eflux.ui.SwitzerlandIcon
import emotion.react.css
import i18next.invoke
import i18next.useTranslation
import mui.icons.material.KeyboardDoubleArrowDownTwoTone
import mui.icons.material.KeyboardDoubleArrowLeftTwoTone
import mui.icons.material.KeyboardDoubleArrowRightTwoTone
import mui.icons.material.KeyboardDoubleArrowUpTwoTone
import mui.material.*
import mui.material.styles.Theme
import mui.material.styles.useTheme
import mui.system.sx
import react.FC
import react.PropsWithChildren
import react.ReactNode
import react.dom.html.ReactHTML.div
import web.cssom.*
import kotlin.math.absoluteValue

external interface CrossborderInfoProps : PropsWithChildren {
    var crossborderNetwork: CrossborderNetwork
}

val CrossborderInfo = FC<CrossborderInfoProps> { props ->
    val theme = useTheme<Theme>()
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light
    val (t, _) = useTranslation()

    val crossborder = props.crossborderNetwork

    val de = crossborder.getLink(Zone("CH"), Zone("DE"))
    val fr = crossborder.getLink(Zone("CH"), Zone("FR"))
    val at = crossborder.getLink(Zone("CH"), Zone("AT"))
    val it = crossborder.getLink(Zone("CH"), Zone("IT"))
    Table {
        sx {
            tableLayout = TableLayout.fixed
        }

        TableRow {
            Cell { }
            Cell {
                title = "${t("variable.link.p.label")} CH-DE"
                placement = TooltipPlacement.top

                Zone {
                    id = "CH-DE"
                    crossborderBranches = de
                    border = Border.North
                }
            }
            Cell { }
        }
        TableRow {
            Cell {
                title = "${t("variable.link.p.label")} CH-FR"
                placement = TooltipPlacement.top

                Zone {
                    id = "CH-FR"
                    crossborderBranches = fr
                    border = Border.West
                }
            }
            Cell {
                title = null
                placement = TooltipPlacement.top

                div {
                    css {
                       position = Position.absolute
                    }

                    SwitzerlandIcon {
                        fill = if(paletteType == PaletteType.light) "rgba(87, 87, 87, 0.2)" else "rgba(230, 230, 230, 0.2)"
                    }
                }

                div {
                    css {
                        position = Position.relative
                        height = (442 / 6).px
                        display = Display.flex
                        justifyContent = JustifyContent.center
                        alignItems = AlignItems.center
                    }
                    Typography {
                        var value = 0.Megawatt
                        de?.netTradeBalance?.let { value += it }
                        fr?.netTradeBalance?.let { value += it }
                        at?.netTradeBalance?.let { value += it }
                        it?.netTradeBalance?.let { value += it }

                        sx {
                            val lightgrey =rgb(167, 167, 167)
                            val darkgrey = rgb(87, 87, 87)

                            color = if(paletteType == PaletteType.dark)
                                if (value > 0.Megawatt) lightgrey else NamedColor.white
                            else
                                if (value > 0.Megawatt) darkgrey else NamedColor.black
                        }

                        val formattedValue = value.amount.toFixed(0) + " " + value.unit.symbol

                        +formattedValue
                    }
                }

            }
            Cell {
                title = "${t("variable.link.p.label")} CH-AT"
                placement = TooltipPlacement.right

                Zone {
                    id = "CH-AT"
                    crossborderBranches = at
                    border = Border.East
                }
            }
        }
        TableRow {
            Cell { }
            Cell {
                title = "${t("variable.link.p.label")} CH-IT"
                placement = TooltipPlacement.bottom

                Zone {
                    id = "CH-IT"
                    crossborderBranches = it
                    border = Border.South
                }
            }
            Cell { }
        }
    }
}

external interface ZoneProps : PropsWithChildren {
    var id: String?
    var crossborderBranches: CrossborderBranches?
    var border: Border?
}

external interface CellProps : PropsWithChildren {
    var title: String?
    var placement: TooltipPlacement?
}

val Cell = FC<CellProps> { props ->
    if (props.title != null) {
        mui.material.Tooltip {
            this.title = ReactNode(props.title!!)
            this.placement = props.placement

            TableCell {
                sx {
                    padding = 0.px
                    borderBottom = None.none
                }

                +props.children
            }
        }
    } else {
        TableCell {
            sx {
                padding = 0.px
                borderBottom = None.none
            }

            +props.children
        }
    }
}

val Zone = FC<ZoneProps> { props ->
    val theme = useTheme<Theme>()
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light

    Typography {
        this.id = id

        val value = props.crossborderBranches?.netTradeBalance
        if (value?.amount != null) {
            sx {
                whiteSpace = WhiteSpace.nowrap

                val lightgrey = rgb(167, 167, 167)
                val darkgrey = rgb(87, 87, 87)
                color = if(paletteType == PaletteType.dark)
                    if (value < 0.Megawatt) lightgrey else NamedColor.white
                else
                    if (value < 0.Megawatt) darkgrey else NamedColor.black
            }

            val formattedValue = value.amount.absoluteValue.toFixed(0) + " " + value.unit.symbol

            css {
                pointerEvents = PointerEvents.all
            }

            val direction = if (value > 0.Megawatt) props.border else
                when (props.border) {
                    Border.North -> {
                        Border.South
                    }

                    Border.South -> {
                        Border.North
                    }

                    Border.East -> {
                        Border.West
                    }

                    Border.West -> {
                        Border.East
                    }

                    null -> {
                        null
                    }
                }

            when (direction) {
                Border.North -> {
                    KeyboardDoubleArrowUpTwoTone {
                        sx {
                            verticalAlign = VerticalAlign.bottom
                        }
                    }

                    +formattedValue
                }

                Border.South -> {
                    KeyboardDoubleArrowDownTwoTone {
                        sx {
                            verticalAlign = VerticalAlign.bottom
                        }
                    }

                    +formattedValue
                }

                Border.East -> {
                    +formattedValue

                    KeyboardDoubleArrowRightTwoTone {
                        sx {
                            verticalAlign = VerticalAlign.bottom
                        }
                    }
                }

                Border.West -> {
                    KeyboardDoubleArrowLeftTwoTone {
                        sx {
                            verticalAlign = VerticalAlign.bottom
                        }
                    }

                    +formattedValue
                }

                null -> {
                }
            }
        }
    }
}